import React from 'react';
import type { ReactNode } from 'react';
import { resolveButtonChrome } from '../../domain/button-theme';
import {
  BUTTON_LINE_HEIGHT,
  buttonLabelPaddingCss,
  resolveButtonSizeFontSize,
} from '../../domain/blocks/button-presentation';
import {
  emailBuilderDisplayedLabelFontWeight,
  emailBuilderRichTextStoredHtml,
  isEmailBuilderRichTextEmpty,
} from '../../editing/richText/emailBuilderRichTextHtml';
import type { ButtonBlock, EmailFontFamily } from '../../domain/types';
import { buttonInnerTableClass, buttonUsesMobileFullWidth, BUTTON_LAYOUT_CSS } from '../../domain/button-layout';
import { blockAlignShell } from '../block-align-shell';
import { padStyle, presentationTableProps, radiusCss } from '../table';
import { linkPropsForMode } from './editor-props';
import { merge } from './merge';
import type { BlockRenderProps } from './types';

export function buttonLabelStyle(
  block: ButtonBlock,
  bodyFontFamily: EmailFontFamily,
  color: string,
) {
  return {
    display: 'inline-block' as const,
    padding: buttonLabelPaddingCss(block.size),
    color,
    fontSize: resolveButtonSizeFontSize(block.size),
    fontWeight: emailBuilderDisplayedLabelFontWeight(block.text, block.fontWeight),
    fontFamily: bodyFontFamily,
    lineHeight: BUTTON_LINE_HEIGHT,
    letterSpacing: 'normal',
    textDecoration: 'none',
    borderRadius: radiusCss(block.borderRadius),
    whiteSpace: 'nowrap' as const,
  };
}

function renderButtonLabelContent(label: string): ReactNode {
  if (!/<[a-z][\s\S]*>/i.test(label)) {
    return label;
  }

  return (
    <span dangerouslySetInnerHTML={{ __html: emailBuilderRichTextStoredHtml("label", label) }} />
  );
}

export { renderButtonLabelContent as renderEmailBuilderButtonLabelContent };

export function renderButtonBlock({
  block,
  textTheme,
  mergeMode,
  editorMode = false,
  labelSlot,
}: BlockRenderProps & { block: ButtonBlock; labelSlot?: ReactNode }) {
  const chrome = resolveButtonChrome(block, textTheme);
  const href = merge(block.href, mergeMode) || '';
  const linkProps = linkPropsForMode(editorMode, href);
  const label = merge(block.text, mergeMode);
  if (!editorMode && isEmailBuilderRichTextEmpty(label)) {
    return null;
  }
  const labelStyle = buttonLabelStyle(
    block,
    textTheme.bodyFontFamily,
    chrome.color,
  );
  const mobileFullWidth = buttonUsesMobileFullWidth(block);

  const buttonTable = (
    <table
      {...presentationTableProps()}
      className={buttonInnerTableClass(block)}
      style={{
        display: block.fullWidth ? 'table' : 'inline-table',
        width: block.fullWidth ? '100%' : undefined,
        borderCollapse: 'collapse',
      }}>
      <tbody>
        <tr>
          <td
            style={{
              backgroundColor: chrome.backgroundColor,
              borderRadius: radiusCss(block.borderRadius),
              ...(block.borderWidth > 0
                ? {
                    boxShadow: `inset 0 0 0 ${block.borderWidth}px ${chrome.borderColor}`,
                  }
                : {}),
              textAlign: 'center',
            }}>
            {labelSlot ??
              (editorMode ? (
                <span
                  className={mobileFullWidth ? BUTTON_LAYOUT_CSS.link : undefined}
                  style={labelStyle}>
                  {renderButtonLabelContent(label)}
                </span>
              ) : linkProps ? (
                <a {...linkProps} className={mobileFullWidth ? BUTTON_LAYOUT_CSS.link : undefined} style={labelStyle}>
                  {renderButtonLabelContent(label)}
                </a>
              ) : (
                <span className={mobileFullWidth ? BUTTON_LAYOUT_CSS.link : undefined} style={labelStyle}>
                  {renderButtonLabelContent(label)}
                </span>
              ))}
          </td>
        </tr>
      </tbody>
    </table>
  );

  return blockAlignShell(block.align, buttonTable, {
    ...padStyle(block.padding),
    lineHeight: 0,
  });
}
